import { Gauge } from "lucide-react";

import { useTranslation } from "@/i18n";
import type { TranslationKey } from "@/i18n";
import { formatPercentID } from "@/lib/format";
import { MetricCard } from "./metric-card";

interface RiskScoreCardProps {
  value: number | null;
  band: string | null;
  isLoading: boolean;
  isError: boolean;
}

function readBand(
  band: string | null,
  value: number | null
): { labelKey: TranslationKey; color: string } {
  if (band === "excellent" || (band == null && (value ?? 0) >= 85)) {
    return {
      labelKey: "dashboard.riskScore.bandExcellent",
      color: "text-success-600",
    };
  }
  if (band === "good" || (band == null && (value ?? 0) >= 70)) {
    return {
      labelKey: "dashboard.riskScore.bandGood",
      color: "text-success-600",
    };
  }
  if (
    band === "needs_attention" ||
    (band == null && value != null && value >= 50)
  ) {
    return {
      labelKey: "dashboard.riskScore.bandNeedsAttention",
      color: "text-warning-600",
    };
  }
  if (band === "critical" || (value != null && value < 50)) {
    return {
      labelKey: "dashboard.riskScore.bandCritical",
      color: "text-danger-600",
    };
  }
  return { labelKey: "dashboard.riskScore.noData", color: "text-neutral-500" };
}

export function RiskScoreCard({
  value,
  band,
  isLoading,
  isError,
}: RiskScoreCardProps) {
  const { t } = useTranslation();
  const { labelKey, color } = readBand(band, value);
  return (
    <MetricCard
      label={t("dashboard.riskScore.label")}
      icon={Gauge}
      isLoading={isLoading}
      isError={isError}
    >
      {value == null ? (
        <div className="text-sm text-neutral-500">
          {t("dashboard.riskScore.noData")}
        </div>
      ) : (
        <>
          <div className="flex items-baseline gap-2">
            <span className={`text-3xl font-medium ${color}`}>
              {formatPercentID(value)}
            </span>
            <span className="text-sm text-neutral-500">/ 100</span>
          </div>
          <div className="mt-2 text-xs text-neutral-600">{t(labelKey)}</div>
        </>
      )}
    </MetricCard>
  );
}
